import {
  ComparisonVerdict,
  SCORE_DELTA_EPSILON,
} from "~/pages/manifold-nextgen/components/evals/constants/comparison";
import { CaseOutcome } from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import type { PartialEvalCase, PartialEvalCaseResult } from "~/services/evals";
import { outcomeForResults } from "./cases";

/** Two runs against each other, case by case. */
export interface ComparisonRow {
  caseId: string;
  caseName: string;
  currentScore: number | null;
  baselineScore: number | null;
  /** `currentScore - baselineScore`, or null when either side has no score. */
  scoreDelta: number | null;
  currentOutcome: CaseOutcome;
  baselineOutcome: CaseOutcome;
  verdict: ComparisonVerdict;
  /**
   * The `case_version` each side ran against, when they disagree — meaning the
   * delta is at least partly the test changing rather than the agent.
   */
  versionDrift: { baseline: number; current: number } | null;
}
export interface RunComparison {
  rows: ComparisonRow[];
  counts: Record<ComparisonVerdict, number>;
}
/**
 * Joins a run against a baseline run, case by case. Only answerable case-wise: a
 * run mean can hold still while one case collapses and another recovers.
 *
 * A case judged in only one of the two runs is `added` or `removed` rather than
 * movement — the two tag selectors need not agree, and calling a case that was
 * not run a regression is what makes a comparison untrustworthy.
 */
export function buildComparison(
  cases: PartialEvalCase[],
  currentResults: PartialEvalCaseResult[],
  baselineResults: PartialEvalCaseResult[]
): RunComparison {
  const nameById = new Map(cases.map((evalCase) => [evalCase.id, evalCase.name]));
  const group = (results: PartialEvalCaseResult[]) => {
    const byCase = new Map<string, PartialEvalCaseResult[]>();
    for (const result of results) {
      const existing = byCase.get(result.case_id);
      if (existing) {
        existing.push(result);
      } else {
        byCase.set(result.case_id, [result]);
      }
    }
    return byCase;
  };

  const current = group(currentResults);
  const baseline = group(baselineResults);
  const counts: Record<ComparisonVerdict, number> = {
    [ComparisonVerdict.IMPROVED]: 0,
    [ComparisonVerdict.REGRESSED]: 0,
    [ComparisonVerdict.UNCHANGED]: 0,
    [ComparisonVerdict.ADDED]: 0,
    [ComparisonVerdict.REMOVED]: 0,
  };

  const rows = [...new Set([...current.keys(), ...baseline.keys()])].map((caseId) => {
    const currentResultsForCase = current.get(caseId) ?? [];
    const baselineResultsForCase = baseline.get(caseId) ?? [];
    const currentCell = outcomeForResults(currentResultsForCase);
    const baselineCell = outcomeForResults(baselineResultsForCase);
    const currentScore = currentCell.meanScore;
    const baselineScore = baselineCell.meanScore;
    const currentVersion = caseVersionOf(currentResultsForCase);
    const baselineVersion = caseVersionOf(baselineResultsForCase);

    let verdict: ComparisonVerdict;
    let scoreDelta: number | null = null;
    if (currentScore == null && baselineScore == null) {
      verdict = ComparisonVerdict.UNCHANGED;
    } else if (baselineScore == null) {
      verdict = ComparisonVerdict.ADDED;
    } else if (currentScore == null) {
      verdict = ComparisonVerdict.REMOVED;
    } else {
      scoreDelta = currentScore - baselineScore;
      verdict =
        scoreDelta > SCORE_DELTA_EPSILON
          ? ComparisonVerdict.IMPROVED
          : scoreDelta < -SCORE_DELTA_EPSILON
            ? ComparisonVerdict.REGRESSED
            : ComparisonVerdict.UNCHANGED;
    }
    counts[verdict] += 1;

    return {
      caseId,
      caseName: nameById.get(caseId) ?? caseId,
      currentScore,
      baselineScore,
      scoreDelta,
      currentOutcome: currentCell.outcome,
      baselineOutcome: baselineCell.outcome,
      verdict,
      versionDrift:
        currentVersion != null && baselineVersion != null && currentVersion !== baselineVersion
          ? { baseline: baselineVersion, current: currentVersion }
          : null,
    };
  });

  return { rows, counts };
}
/**
 * The case version a set of results ran against, or null when they disagree — a
 * case edited mid-run, where picking one of the two would be a guess.
 */
function caseVersionOf(results: PartialEvalCaseResult[]): number | null {
  if (results.length === 0) {
    return null;
  }
  const versions = new Set(results.map((result) => result.case_version));
  return versions.size === 1 ? results[0].case_version : null;
}
